import React, { ChangeEvent } from 'react';
import { AddBoxOutlined, IndeterminateCheckBoxOutlined } from '@material-ui/icons';
import { ascendingComparator } from '../../document-index/DocumentIndexUtils';
import { TreeItem, TreeView } from '@material-ui/lab';
import clsx from 'clsx';
import { hasPermissionToView } from '../../../scripts/store-utils';
import { makeStyles } from '@material-ui/core/styles';
import { ManagePermissionsDialogType } from '../../design/ManagePermissionsDialog';
import { ICompany } from '../../../api-client/autogenerated';
import { sortLastNamesDesc } from '../../../scripts/utils';
import LabelWithCheckbox from '../../custom-components/LabelWithCheckbox';

const useStyles = makeStyles({
  treeRoot: {
    marginTop: 16,
    padding: 6,
    border: '1px solid #949494',
    borderRadius: '4px',
  },
  treeLabel: {
    padding: 0,
    fontSize: '16px',
    lineHeight: '24px',
  },
  treeLabelRoot: {
    fontWeight: 600,
  },
  treeLabelParent: {
    fontWeight: 500,
  },
  treeLabelChild: {
    fontWeight: 400,
  },
  treeGroup: {
    marginLeft: '22px',
  },
  // A node's expand icon sits on the first line of its label, not centered against every line when
  // the label wraps. The icon is 18px and the label's lines are 24px.
  treeContent: {
    alignItems: 'flex-start',
  },
  treeIconContainer: {
    marginTop: 3,
  },
});

type Props = {
  type?: ManagePermissionsDialogType;
  companies: ICompany[];
  selectedUsers?: string[];
  /** Company ids to start expanded. Only read when the tree mounts; remount it to change them. */
  defaultExpanded?: string[];
  handleCheckCompany: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    companyId: string,
  ) => void;
  handleCheckNestedUser: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    userId: string,
    companyId: string,
  ) => void;
};

export default function CompanyUserTreeView(props: Props) {
  const classes = useStyles();
  const {
    type = ManagePermissionsDialogType.None,
    companies,
    selectedUsers,
    defaultExpanded,
    handleCheckCompany,
    handleCheckNestedUser,
  } = props;

  return (
    <TreeView
      multiSelect
      defaultExpanded={defaultExpanded}
      defaultCollapseIcon={<IndeterminateCheckBoxOutlined />}
      defaultExpandIcon={<AddBoxOutlined />}
      className={classes.treeRoot}
    >
      {companies
        .sort((a, b) => ascendingComparator(a, b, 'name'))
        .map((company) => (
          <TreeItem
            key={company.id}
            nodeId={company.id}
            label={
              <LabelWithCheckbox
                alignTop
                label={company.name}
                indeterminate={
                  !!selectedUsers?.some(
                    (userId) => !!company.users?.some(({ id }) => id === userId),
                  ) && !company?.users?.every(({ id }) => selectedUsers?.includes(id))
                }
                checked={!!company?.users?.every(({ id }) => !!selectedUsers?.includes(id))}
                onChange={(e, checked) => handleCheckCompany(e, checked, company.id)}
              />
            }
            classes={{
              label: clsx(classes.treeLabel, classes.treeLabelParent),
              group: classes.treeGroup,
              content: classes.treeContent,
              iconContainer: classes.treeIconContainer,
            }}
          >
            {company.users
              ?.sort((a, b) => sortLastNamesDesc(a.name, b.name))
              .map((user) => (
                <TreeItem
                  key={user.id}
                  nodeId={user.id}
                  label={
                    <LabelWithCheckbox
                      alignTop
                      label={user.name + ` (${user.email})`}
                      strikethrough={!hasPermissionToView(type, user)}
                      checked={!!selectedUsers?.includes(user.id)}
                      onChange={(e, checked) =>
                        handleCheckNestedUser(e, checked, user.id, company.id)
                      }
                    />
                  }
                  classes={{
                    label: clsx(classes.treeLabel, classes.treeLabelChild),
                    group: classes.treeGroup,
                    content: classes.treeContent,
                    iconContainer: classes.treeIconContainer,
                  }}
                />
              ))}
          </TreeItem>
        ))}
    </TreeView>
  );
}
